// @ts-strict-ignore
import * as React from 'react';
import { iconAndAltForIntegrationType, isBetaForIntegrationType } from '../helpers/integrationHelpers';
import {
    AppPlatform,
    GoogleCalendarIntegrationData,
    Integration,
    IntegrationCapability,
    IntegrationErrorType,
    IntegrationFirebaseBeta,
    IntegrationInstallationId,
    IntegrationProvider,
    IntegrationProviderBitbucket,
    IntegrationProviderBugsnag,
    IntegrationProviderDynatrace,
    IntegrationProviderGoogle,
    IntegrationProviderJira,
    IntegrationProviderLinear,
    IntegrationProviderMonday,
    IntegrationProviderSentry,
    IntegrationProviderShortcut,
    IntegrationType,
    JenkinsIntegrationData,
    JiraIntegrationData,
    MondayBoard,
    MondayBoardColumnPair,
    ReleaseStepType,
    WorkflowEnabledSteps,
    WorkflowType,
} from '../types';
import { AppComponentProps, StringKeyedDictionary } from '../typescriptHelpers';
import EditIntegrationModal from './EditIntegrationModal';
import { antGrey, runwayColors, tagStyle } from './styles/globalStyles';
import { CopyOutlined, EditOutlined, InfoCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { DatetimeFormat, formatDateWithTimezone } from '../timeHelpers';
import styles from './styles/settingsStyles';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Descriptions from 'antd/lib/descriptions';
import Button from 'antd/lib/button';
import Popconfirm from 'antd/lib/popconfirm';
import Paragraph from 'antd/lib/typography/Paragraph';
import {
    selectApp,
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
    selectOrgProvidersById,
    selectWorkflowEnabledSteps,
} from '../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { UpdateIntegrationRequestDataType } from '../actions/integrations';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../helpers/aclHelpers';
import { copyToClipboard, displayTextForIntegrationStatus, tagColorForIntegrationStatus } from '../helpers';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import { codeStyle, eventPropertyTerminology, listEventsAndParams } from '../helpers/displayHelpers';
import { BetaBadge } from './automations/AppAutomationSettings';
import dayjs from 'dayjs';
import { globalMessage } from '@/utils/globalMessage';
import * as AnalyticEvents from '@/utils/analytics';
import Tooltip from 'antd/lib/tooltip';
import { Avatar, Flex, Form, Input, Modal } from 'antd/lib';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';

interface ConnectedIntegrationsProps extends AppComponentProps {
    integrations: Integration[];
    trackingProperties: StringKeyedDictionary<any>;
    updateIntegrationIsLoading: StringKeyedDictionary<boolean>;
    updateIntegrationSuccess: StringKeyedDictionary<boolean>;
    updateIntegration: (
        integrationId: IntegrationInstallationId,
        integrationType: IntegrationType,
        data: UpdateIntegrationRequestDataType,
    ) => void;
    updateIntegrationAliasIsLoading: StringKeyedDictionary<boolean>;
    updateIntegrationAliasFailed: StringKeyedDictionary<string>;
    updateIntegrationAliasSucceeded: StringKeyedDictionary<boolean>;
    updateIntegrationAlias: (integrationId: IntegrationInstallationId, alias: string) => void;
    showCompleteIntegrationsModal: (integrationId: IntegrationInstallationId) => void;
    deleteIntegration: (integrationId: IntegrationInstallationId, integrationType: IntegrationType) => void;
    deleteIntegrationIsLoading: StringKeyedDictionary<boolean>;
    capability: IntegrationCapability;
    capabilityDisabled: boolean;
}

const ConnectedIntegrationsComponent = (props: ConnectedIntegrationsProps) => {
    const isShowingInitialState = {};
    const isShowingInitialUpdateIntegrationAliasState = {};

    props.integrations.forEach((integration) => {
        isShowingInitialState[integration.installationId] = false;
        isShowingInitialUpdateIntegrationAliasState[integration.installationId] = false;
    });

    const [integrationEditModalIsShowing, setIntegrationEditModalIsShowing] = React.useState(isShowingInitialState);
    const [updateIntegrationAliasModalIsVisible, setUpdateIntegrationAliasModalIsVisible] = React.useState(
        isShowingInitialUpdateIntegrationAliasState,
    );

    const integrationProviders = useSelector((state: RootState) => selectOrgProvidersById(state, props.orgId));
    const appSettings = useSelector((state: RootState) => selectApp(state, props.appId)).appSettings;
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, props.appId));

    const onCancelEditIntegration = (integrationId: IntegrationInstallationId) => {
        const newState = {
            ...integrationEditModalIsShowing,
            [integrationId]: false,
        };

        setIntegrationEditModalIsShowing(newState);
    };

    React.useEffect(() => {
        const updatedIntegrationEditModalIsShowing = {
            ...integrationEditModalIsShowing,
        };
        props.integrations.forEach((integration) => {
            const isShowing = integrationEditModalIsShowing[integration.installationId];
            const didUpdate = props.updateIntegrationSuccess[integration.installationId];

            if (isShowing && didUpdate) {
                updatedIntegrationEditModalIsShowing[integration.installationId] = false;
            }
        });

        setIntegrationEditModalIsShowing(updatedIntegrationEditModalIsShowing);
    }, [props.updateIntegrationSuccess]);

    React.useEffect(() => {
        const updatedUpdateIntegrationAliasModalIsVisible = {
            ...updateIntegrationAliasModalIsVisible,
        };
        props.integrations.forEach((integration) => {
            const isShowing = updateIntegrationAliasModalIsVisible[integration.installationId];
            const didUpdate =
                props.updateIntegrationAliasSucceeded[integration.installationId] ||
                props.updateIntegrationAliasFailed[integration.installationId];

            if (isShowing && didUpdate) {
                updatedUpdateIntegrationAliasModalIsVisible[integration.installationId] = false;
            }
        });

        setUpdateIntegrationAliasModalIsVisible(updatedUpdateIntegrationAliasModalIsVisible);
    }, [props.updateIntegrationAliasSucceeded, props.updateIntegrationAliasFailed]);

    return (
        <Space direction="vertical" size="middle">
            {props.integrations
                // TODO: Support simultaneous org and app entity updates
                // When deleting the last integration from a provider, both app and org entities are updated on the BE
                // However, those entities are updated at separate places in the FE, which sometimes leads to inconsistencies
                // This means that the org provider can be removed before the app integration, so we need to handle this case
                .filter((integration) => !!integrationProviders[integration.providerId])
                .map((integration) => {
                    const provider = integrationProviders[integration.providerId];
                    const integrationName = integration.name;
                    const integrationAlias =
                        typeof integration.alias === 'string' && integration.alias.trim().length > 0 ?
                            integration.alias.trim()
                        :   null;
                    const statusText = displayTextForIntegrationStatus(integration);
                    const statusTag = (
                        <Tag color={tagColorForIntegrationStatus(integration)} style={tagStyle}>
                            {statusText}
                        </Tag>
                    );

                    const editButton = editButtonForIntegration(integration, props.capabilityDisabled, () => {
                        const newState = {
                            ...integrationEditModalIsShowing,
                            [integration.installationId]: true,
                        };

                        setIntegrationEditModalIsShowing(newState);
                    });
                    const completeSetupButton = completeIntegrationButtonForIntegration(
                        integration.installationId,
                        props.capabilityDisabled,
                        () => {
                            props.showCompleteIntegrationsModal(integration.installationId);
                        },
                    );

                    const deleteButtonIsLoading = props.deleteIntegrationIsLoading[integration.installationId];
                    const deleteButton = deleteButtonForIntegration(
                        integration.installationId,
                        deleteButtonIsLoading,
                        integration.capabilities,
                        (integrationId: IntegrationInstallationId) => {
                            props.deleteIntegration(integrationId, integration.id);
                        },
                    );

                    const updateIntegrationAliasButtonIsLoading =
                        props.updateIntegrationAliasIsLoading[integration.installationId];
                    const updateIntegrationAliasButtonFailed =
                        props.updateIntegrationAliasFailed[integration.installationId];
                    const updateIntegrationAliasButtonSucceeded =
                        props.updateIntegrationAliasSucceeded[integration.installationId];
                    const updateIntegrationAliasButton = updateIntegrationAliasButtonForIntegration(
                        integration.installationId,
                        updateIntegrationAliasButtonIsLoading,
                        (integrationId: IntegrationInstallationId) => {
                            const newState = {
                                ...updateIntegrationAliasModalIsVisible,
                                [integrationId]: true,
                            };
                            setUpdateIntegrationAliasModalIsVisible(newState);
                        },
                    );
                    let primaryActionButton;
                    let needsEditModal = true;

                    if (integration.integrationError) {
                        switch (integration.integrationError.status) {
                            case IntegrationErrorType.AuthInvalid:
                                primaryActionButton = null;
                                needsEditModal = false;
                                break;
                            case IntegrationErrorType.IncompleteData:
                                primaryActionButton = completeSetupButton;
                                needsEditModal = false;
                                break;
                            case IntegrationErrorType.IncorrectData:
                            case IntegrationErrorType.InternalError:
                            case IntegrationErrorType.Unreachable:
                                primaryActionButton = editButton;
                                break;
                        }
                    } else {
                        primaryActionButton = editButton;
                    }

                    const editModalIsVisible = integrationEditModalIsShowing[integration.installationId];
                    const aliasModalIsVisible = updateIntegrationAliasModalIsVisible[integration.installationId];
                    const isLoading = props.updateIntegrationIsLoading[integration.installationId];
                    const displayFields = displayDataForIntegration(
                        workflowEnabledSteps,
                        integration,
                        appSettings.platform,
                        appSettings.storeEmailForwardingAddress,
                        props.trackingProperties,
                        provider,
                        effectiveTimezone,
                        use24HourTimeFormat,
                    );
                    const [icon, alt] = iconAndAltForIntegrationType(integration.id);
                    const statusMessage = integration.integrationError?.message;
                    const url = integration.integrationError?.url;
                    const urlTarget = integration.integrationError?.urlOpensInNewTab === true ? '_blank' : '';
                    const urlText = integration.integrationError?.urlText;

                    return (
                        <div key={integration.installationId}>
                            <Flex vertical>
                                <Flex justify="space-between" align="center">
                                    <Flex align="center" gap="16px">
                                        <Flex align="center" gap="8px">
                                            <Avatar
                                                src={icon}
                                                alt={alt}
                                                size={30}
                                                shape="square"
                                                style={{ border: 'none' }}
                                            />
                                            <Flex align="baseline" gap="6px">
                                                <Text style={{ fontSize: 16 }}>{integrationName}</Text>
                                                {integrationAlias && (
                                                    <Text type="secondary" style={{ fontSize: 14, marginLeft: 2 }}>
                                                        {integrationAlias}
                                                    </Text>
                                                )}
                                                {updateIntegrationAliasButton}
                                            </Flex>
                                        </Flex>
                                        <Flex align="center" gap="6px">
                                            {isBetaForIntegrationType(integration.id) && <BetaBadge />}
                                            {statusTag}
                                        </Flex>
                                    </Flex>
                                    <Flex justify="flex-end" flex={1}>
                                        {primaryActionButton}
                                        {deleteButton}
                                    </Flex>
                                </Flex>
                                {statusMessage && (
                                    <div
                                        style={{
                                            marginTop: 16,
                                            maxWidth: 1024,
                                        }}
                                    >
                                        <Text>{statusMessage}</Text>
                                        {url && (
                                            <Button icon={<LinkOutlined />} type="link" href={url} target={urlTarget}>
                                                {urlText}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </Flex>
                            {displayFields && (
                                <PlanRestrictedFeatureWrapper
                                    integrationCapability={props.capability}
                                    shouldFakeDisabledState
                                >
                                    {() => (
                                        <Descriptions
                                            size="middle"
                                            bordered
                                            style={{
                                                ...styles.descriptionsWidthStyle,
                                                marginBottom: 18,
                                                marginTop: 18,
                                            }}
                                            column={1}
                                            labelStyle={{ ...styles.descriptionsLabelStyle }}
                                        >
                                            {displayFields.map((item, index) => {
                                                return (
                                                    <Descriptions.Item
                                                        label={
                                                            <>
                                                                <span>{item.label}</span>
                                                                {item.labelExtra}
                                                            </>
                                                        }
                                                        key={index}
                                                        contentStyle={{
                                                            color:
                                                                item.displayString ?
                                                                    runwayColors.darkTextGrey
                                                                :   antGrey.grey6,
                                                        }}
                                                    >
                                                        <Paragraph
                                                            style={{ marginBottom: 0 }}
                                                            ellipsis={{ rows: 2, expandable: true }}
                                                        >
                                                            {item.displayString ?? 'Not set'}
                                                        </Paragraph>
                                                    </Descriptions.Item>
                                                );
                                            })}
                                        </Descriptions>
                                    )}
                                </PlanRestrictedFeatureWrapper>
                            )}
                            {needsEditModal && (
                                <EditIntegrationModal
                                    orgId={props.orgId}
                                    appId={props.appId}
                                    integration={integration}
                                    open={editModalIsVisible}
                                    isLoading={isLoading}
                                    updateIntegration={props.updateIntegration}
                                    onCancel={onCancelEditIntegration}
                                />
                            )}
                            <UpdateIntegrationAliasModal
                                open={aliasModalIsVisible}
                                currentAlias={integration.alias}
                                isLoading={updateIntegrationAliasButtonIsLoading}
                                updateDidSucceed={updateIntegrationAliasButtonSucceeded}
                                updateErrorMessage={updateIntegrationAliasButtonFailed}
                                integrationName={integrationName}
                                onSubmit={(alias: string) => {
                                    props.updateIntegrationAlias(integration.installationId, alias);
                                    setUpdateIntegrationAliasModalIsVisible({});
                                }}
                                onCancel={() => {
                                    const newState = {
                                        ...updateIntegrationAliasModalIsVisible,
                                        [integration.installationId]: false,
                                    };
                                    setUpdateIntegrationAliasModalIsVisible(newState);
                                }}
                            />
                        </div>
                    );
                })}
        </Space>
    );
};

export default ConnectedIntegrationsComponent;

const editButtonForIntegration = (integration: Integration, disabled: boolean, onClick: (e) => void) => {
    switch (integration.id) {
        case IntegrationType.Slack:
            return (
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAAddRemoveEditIntegration}
                    disabled={disabled}
                    onClick={onClick}
                >
                    Edit
                </PermissionedActionButton>
            );
        case IntegrationType.CustomAnalytics:
            return null;
        default:
            return (
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAAddRemoveEditIntegration}
                    onClick={onClick}
                    disabled={disabled}
                >
                    Edit
                </PermissionedActionButton>
            );
    }
};

const completeIntegrationButtonForIntegration = (
    _: IntegrationInstallationId,
    disabled: boolean,
    onClick: (e) => void,
) => {
    return (
        <Button type="primary" onClick={onClick} disabled={disabled}>
            Complete setup
        </Button>
    );
};

const deleteButtonForIntegration = (
    integrationId: IntegrationInstallationId,
    isLoading: boolean,
    capabilities: IntegrationCapability[],
    onConfirm: (IntegrationType) => void,
) => {
    const warningMessages = [];
    if (capabilities.includes(IntegrationCapability.Notifications)) {
        warningMessages.push('Removing this integration will result in removing all notification channel overrides.');
    }
    if (capabilities.includes(IntegrationCapability.FeatureManagement)) {
        warningMessages.push(
            'Removing this integration will result in removing any associated feature affiliation patterns.',
        );
    }

    const popconfirmTitle = ['Are you sure you want to remove this integration?', ...warningMessages].join(' ');
    return (
        <div style={{ marginLeft: 8 }}>
            <Popconfirm
                title={popconfirmTitle}
                okText="Yes, remove"
                placement="topRight"
                okButtonProps={{ danger: true }}
                onConfirm={() => onConfirm(integrationId)}
            >
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAAddRemoveEditIntegration}
                    danger
                    loading={isLoading}
                    disabled={false}
                >
                    Remove
                </PermissionedActionButton>
            </Popconfirm>
        </div>
    );
};

const updateIntegrationAliasButtonForIntegration = (
    integrationId: IntegrationInstallationId,
    isLoading: boolean,
    onClick: (integrationId: IntegrationInstallationId) => void,
) => {
    return (
        <Button
            loading={isLoading}
            disabled={isLoading}
            icon={
                <EditOutlined
                    style={{
                        color: runwayColors.info,
                        fontSize: 13,
                    }}
                />
            }
            size="small"
            ghost
            onClick={() => onClick(integrationId)}
        />
    );
};

interface UpdateIntegrationAliasModalProps {
    open: boolean;
    currentAlias: string | null;
    isLoading: boolean;
    updateDidSucceed: boolean;
    updateErrorMessage: string;
    integrationName: string;
    onSubmit: (alias: string) => void;
    onCancel: () => void;
}

const UpdateIntegrationAliasModal: React.FC<UpdateIntegrationAliasModalProps> = ({
    open,
    currentAlias,
    isLoading,
    updateDidSucceed,
    updateErrorMessage,
    integrationName,
    onSubmit,
    onCancel,
}) => {
    const [alias, setAlias] = React.useState(currentAlias ?? '');

    React.useEffect(() => {
        if (open) {
            setAlias(currentAlias ?? '');
        }
    }, [open, currentAlias]);

    React.useEffect(() => {
        if (updateDidSucceed) {
            globalMessage.success(`Alias updated successfully${integrationName ? ` for ${integrationName}` : ''}!`);
        } else if (updateErrorMessage) {
            globalMessage.error(
                `Failed to update alias${integrationName ? ` for ${integrationName}` : ''}: ${updateErrorMessage}`,
            );
        }
    }, [updateDidSucceed, updateErrorMessage]);

    return (
        <Modal
            title={`Assign installation alias ${integrationName ? `for ${integrationName}` : ''}`}
            open={open}
            onCancel={onCancel}
            onOk={() => onSubmit(alias)}
            confirmLoading={isLoading}
            okText="Save"
        >
            <Form layout="vertical">
                <Form.Item
                    label="Installation alias"
                    help={`An optional display alias to uniquely identify this specific ${integrationName ? integrationName : 'integration'} installation in the Runway UI.`}
                >
                    <Input value={alias} onChange={(e) => setAlias(e.target.value)} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

interface DisplayDataForIntegration {
    label: string;
    labelExtra?: React.ReactNode;
    displayString?: string | React.ReactNode;
}

const schedulingIntegrationLabelExtra = () => {
    return (
        <Tooltip
            title={
                <>
                    Runway will sync your pilot rotation with your scheduling system, if the integration is enabled.
                    Note that Runway needs an active release with a kickoff date or target kickoff date in order to line
                    up with your scheduling system. View more in{' '}
                    <a
                        href={
                            'https://docs.runway.team/using-runway/app-settings/release-pilot-rotation#using-a-scheduling-integration-to-manage-the-release-pilot-rotation'
                        }
                        target="_blank"
                    >
                        our docs
                    </a>
                    .
                </>
            }
        >
            <InfoCircleOutlined style={{ color: antGrey.grey6, cursor: 'pointer', marginLeft: 8 }} />
        </Tooltip>
    );
};

const displayDataForIntegration: (
    workflowEnabledSteps: WorkflowEnabledSteps,
    integration: Integration,
    platform: AppPlatform,
    storeEmailForwardingAddress: string,
    trackingProperties: StringKeyedDictionary<any>,
    integrationProvider: IntegrationProvider,
    effectiveTimezone: string,
    use24HourTimeFormat?: boolean,
) => DisplayDataForIntegration[] = (
    workflowEnabledSteps,
    integration,
    platform,
    storeEmailForwardingAddress,
    trackingProperties,
    integrationProvider,
    effectiveTimezone,
    use24HourTimeFormat,
) => {
    const rcWorkflowLabel = (workflowNoun?: string) => {
        return (platform === AppPlatform.ReactNativeOTA ? 'Staging release ' : 'CI ') + (workflowNoun ?? 'workflow');
    };

    const prodWorkflowLabel = (workflowNoun?: string) => {
        return 'Release ' + (workflowNoun ?? 'workflow');
    };

    const rcBuildName = platform === AppPlatform.ReactNativeOTA ? 'Staging' : 'CI';
    const isWorkflowCIStepEnabled = workflowEnabledSteps[ReleaseStepType.ReleaseCandidate];

    switch (integration.id) {
        case IntegrationType.GitHubVCSDistribution:
        case IntegrationType.GitHubVCS:
            return [
                {
                    label: 'Repository name',
                    displayString: integration.data?.repoName,
                },
            ];
        case IntegrationType.GitHubCI: {
            const fields = [
                {
                    label: 'Repository name',
                    displayString: integration.data?.repoName,
                },
                {
                    label: rcWorkflowLabel(),
                    displayString: integration.data?.workflowName,
                },
            ];

            if (!isWorkflowCIStepEnabled && integration.data?.devWorkflowName) {
                fields.push({
                    label: 'Development workflow',
                    displayString: integration.data?.devWorkflowName,
                });
            }

            if (!isWorkflowCIStepEnabled && integration.data?.prodWorkflowName) {
                fields.push({ label: prodWorkflowLabel(), displayString: integration.data.prodWorkflowName });
            }

            return fields;
        }
        case IntegrationType.GitHubIT: {
            const fields = [
                {
                    label: 'Repository name',
                    displayString: integration.data?.repoName,
                },
            ];

            const additionalDoneStates = integration.data?.additionalDoneStates;
            if (additionalDoneStates && additionalDoneStates.length > 0) {
                fields.push({
                    label: 'Additional done states',
                    displayString: integration.data?.additionalDoneStates.join(', '),
                });
            }

            return fields;
        }
        case IntegrationType.Bitbucket: {
            const bitbucketProvider = integrationProvider as IntegrationProviderBitbucket;
            const workspaceSlug =
                integration.data?.selectedRepo ?
                    (bitbucketProvider?.data?.availableWorkspacesAndRepos?.find(
                        (workspace) => workspace.workspaceId === integration.data?.selectedRepo?.workspaceId,
                    )?.workspaceSlug ?? null)
                :   null;
            return [
                {
                    label: 'Workspace/Repository',
                    displayString: workspaceSlug?.concat('/', integration.data?.selectedRepo?.name),
                },
            ];
        }
        case IntegrationType.BitbucketCI: {
            const bitbucketProvider = integrationProvider as IntegrationProviderBitbucket;
            const workspaceSlug =
                integration.data?.selectedRepo ?
                    (bitbucketProvider?.data?.availableWorkspacesAndRepos?.find(
                        (workspace) => workspace.workspaceId === integration.data?.selectedRepo?.workspaceId,
                    )?.workspaceSlug ?? null)
                :   null;
            const fields = [
                {
                    label: 'Workspace/Repository',
                    displayString: workspaceSlug?.concat('/', integration.data?.selectedRepo?.name),
                },
                {
                    label: `${rcBuildName} pipeline`,
                    displayString: integration.data?.workflowName,
                },
            ];

            if (!isWorkflowCIStepEnabled && integration.data?.devWorkflowName) {
                fields.push({
                    label: 'Development pipeline',
                    displayString: integration.data?.devWorkflowName,
                });
            }

            if (!isWorkflowCIStepEnabled && integration.data?.prodWorkflowName) {
                fields.push({ label: 'Release pipeline', displayString: integration.data.prodWorkflowName });
            }

            return fields;
        }
        case IntegrationType.GitLabVCS:
            return [
                {
                    label: 'Project',
                    displayString: integration.data?.project?.displayableName,
                },
            ];
        case IntegrationType.GitLabCI: {
            const fields = [
                {
                    label: 'Project',
                    displayString: integration.data?.project?.displayableName,
                },
                {
                    label: `${rcBuildName} deploy job`,
                    displayString: integration.data?.workflowName,
                },
            ];

            if (!isWorkflowCIStepEnabled && integration.data?.devWorkflowName) {
                fields.push({
                    label: 'Development deploy job',
                    displayString: integration.data?.devWorkflowName,
                });
            }

            if (!isWorkflowCIStepEnabled && integration.data?.prodWorkflowName) {
                fields.push({ label: 'Release job', displayString: integration.data.prodWorkflowName });
            }

            return fields;
        }
        case IntegrationType.TestFlight:
            return [
                {
                    label: 'Bundle ID',
                    displayString: integration.data?.bundleId,
                },
            ];
        case IntegrationType.Apple: {
            const fields = [
                {
                    label: 'Bundle ID',
                    displayString: integration.data?.bundleId,
                },
                {
                    label: 'App Store Connect email forwarding address',
                    displayString: (
                        <Space>
                            <Text>{storeEmailForwardingAddress}</Text>
                            <Button
                                icon={<LinkOutlined style={{ color: runwayColors.primary }} />}
                                type="link"
                                onClick={() => {
                                    navigator.clipboard.writeText(storeEmailForwardingAddress);
                                    globalMessage.success('Address copied to clipboard!');
                                    AnalyticEvents.track(
                                        AnalyticEvents.Event.ClickCopyStoreEmailForwardingAddressLink,
                                        trackingProperties,
                                    );
                                }}
                            ></Button>
                        </Space>
                    ),
                },
            ];

            if (integration.data?.lastReceivedForwardedEmailAt) {
                const lastReceivedAt = dayjs(integration.data.lastReceivedForwardedEmailAt);
                fields.push({
                    label: 'App Store Connect email forwarding',
                    displayString: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            Active (Last received:{' '}
                            <TimeWithConditionalTz
                                triggeredAtLabel={formatDateWithTimezone(
                                    integration.data.lastReceivedForwardedEmailAt,
                                    'shortWithYearAndComma',
                                    effectiveTimezone,
                                    use24HourTimeFormat,
                                )}
                                triggeredByMoment={lastReceivedAt}
                                effectiveTimezone={effectiveTimezone}
                                use24HourTimeFormat={!!use24HourTimeFormat}
                            />
                            )
                        </div>
                    ),
                });
            }

            return fields;
        }
        case IntegrationType.AzureCI: {
            const fields = [
                {
                    label: 'Project',
                    displayString:
                        integration.data?.selectedProject ?
                            integration.data?.selectedProject?.orgId.concat(
                                '/',
                                integration.data?.selectedProject?.name,
                            )
                        :   null,
                },
                {
                    label: `${rcBuildName} pipeline`,
                    displayString: integration.data?.workflowName,
                },
            ];

            if (!isWorkflowCIStepEnabled && integration.data?.devWorkflowName) {
                fields.push({
                    label: 'Development pipeline',
                    displayString: integration.data?.devWorkflowName,
                });
            }

            if (!isWorkflowCIStepEnabled && integration.data?.prodWorkflowName) {
                fields.push({ label: 'Release pipeline', displayString: integration.data.prodWorkflowName });
            }

            return fields;
        }
        case IntegrationType.AzureIT: {
            const fields = [
                {
                    label: 'Project',
                    displayString:
                        integration.data?.selectedProject ?
                            integration.data?.selectedProject?.orgId.concat(
                                '/',
                                integration.data?.selectedProject?.name,
                            )
                        :   null,
                },
            ];

            const workAreas = integration.data?.selectedWorkAreas;
            if (workAreas && workAreas.length > 0) {
                fields.push({
                    label: 'Work areas',
                    displayString: integration.data?.selectedWorkAreas.map((workArea) => workArea.path).join(', '),
                });
            }

            const doneColumns = integration.data?.additionalDoneColumns;
            if (doneColumns && doneColumns.length > 0) {
                fields.push({
                    label: 'Additional done columns',
                    displayString: integration.data?.additionalDoneColumns
                        .map((doneColumn) => doneColumn.name)
                        .join(', '),
                });
            }

            return fields;
        }
        case IntegrationType.Codemagic: {
            const fields = [
                {
                    label: rcWorkflowLabel(),
                    displayString: integration.data?.appName?.concat('/', integration.data?.workflowName ?? ''),
                },
            ];

            if (!isWorkflowCIStepEnabled && integration.data?.devWorkflowName) {
                fields.push({
                    label: 'Development workflow',
                    displayString: integration.data?.devAppName?.concat('/', integration.data?.devWorkflowName),
                });
            }

            if (!isWorkflowCIStepEnabled && integration.data?.prodWorkflowName) {
                fields.push({
                    label: prodWorkflowLabel(),
                    displayString: integration.data?.prodAppName?.concat('/', integration.data?.prodWorkflowName),
                });
            }

            return fields;
        }
        case IntegrationType.Bitrise: {
            const isPipeline =
                integration.data?.isPipeline && integration.data?.isPipeline[WorkflowType.Staging] == true;
            const fields = [
                {
                    label: rcWorkflowLabel(isPipeline ? 'pipeline' : null),
                    displayString: integration.data?.appTitle?.concat('/', integration.data?.workflowName ?? ''),
                },
            ];

            if (!isWorkflowCIStepEnabled && integration.data?.devWorkflowName) {
                const isPipeline =
                    integration.data?.isPipeline && integration.data?.isPipeline[WorkflowType.Working] == true;
                const label = 'Development ' + (isPipeline ? 'pipeline' : 'workflow');
                fields.push({
                    label: label,
                    displayString: integration.data?.devAppTitle?.concat('/', integration.data?.devWorkflowName),
                });
            }

            if (!isWorkflowCIStepEnabled && integration.data?.prodWorkflowName) {
                const isPipeline =
                    integration.data?.isPipeline && integration.data?.isPipeline[WorkflowType.Production] == true;
                fields.push({
                    label: prodWorkflowLabel(isPipeline ? 'pipeline' : null),
                    displayString: integration.data?.prodAppTitle?.concat('/', integration.data?.prodWorkflowName),
                });
            }

            return fields;
        }
        case IntegrationType.Buildkite: {
            const fields = [
                {
                    label: 'Buildkite org',
                    displayString:
                        integration.data?.org ? `${integration.data.org.name} (${integration.data.org.slug})` : null,
                },
                {
                    label: `${rcBuildName} pipeline`,
                    displayString: integration.data?.workflowName,
                },
            ];

            if (!isWorkflowCIStepEnabled && integration.data?.devWorkflowName) {
                fields.push({
                    label: 'Development pipeline',
                    displayString: integration.data?.devWorkflowName,
                });
            }

            if (!isWorkflowCIStepEnabled && integration.data?.prodWorkflowName) {
                fields.push({ label: 'Release pipeline', displayString: integration.data.prodWorkflowName });
            }

            return fields;
        }
        case IntegrationType.Bugsnag: {
            const bugsnagProvider = integrationProvider as IntegrationProviderBugsnag;
            const orgName =
                integration.data?.selectedProject ?
                    (bugsnagProvider.data.availableOrgsAndProjects.find(
                        (org) => org.orgId === integration.data?.selectedProject?.orgId,
                    )?.orgName ?? null)
                :   null;

            const fields = [
                {
                    label: 'Organization',
                    displayString: orgName,
                },
                {
                    label: 'Project',
                    displayString:
                        integration.data?.selectedProject ? `${integration.data.selectedProject.name}` : null,
                },
                {
                    label: 'Prod release stage',
                    displayString: integration.data.prodReleaseStage,
                },
            ];

            if (integration.data?.betaReleaseStage) {
                fields.push({ label: 'Beta release stage', displayString: integration.data.betaReleaseStage });
            }

            return fields;
        }
        case IntegrationType.Sentry: {
            const sentryProvider = integrationProvider as IntegrationProviderSentry;
            const orgName =
                integration.data?.selectedProject ?
                    (sentryProvider.data.availableOrgsAndProjects.find(
                        (org) => org.orgId === integration.data?.selectedProject?.orgId,
                    )?.orgName ?? null)
                :   null;

            const fields = [
                {
                    label: 'Organization',
                    displayString: orgName,
                },
                {
                    label: 'Project',
                    displayString:
                        integration.data?.selectedProject ?
                            `${integration.data.selectedProject.displayableName}`
                        :   null,
                },
                {
                    label: 'Production environment',
                    displayString: integration.data.prodEnvironment,
                },
            ];

            if (integration.data?.betaEnvironment) {
                fields.push({ label: 'Beta environment', displayString: integration.data.betaEnvironment });
            }

            return fields;
        }
        case IntegrationType.EmbraceStabilityMonitoring: {
            const fields = [
                {
                    label: 'Production app',
                    displayString:
                        integration.data?.selectedProdApp ? `${integration.data.selectedProdApp.name}` : null,
                },
            ];

            if (integration.data?.selectedBetaApp) {
                fields.push({ label: 'Beta app', displayString: integration.data.selectedBetaApp.name });
            }

            return fields;
        }
        case IntegrationType.TestRail: {
            const fields = [
                {
                    label: 'Project',
                    displayString:
                        integration.data?.selectedProject ? `${integration.data.selectedProject.name}` : null,
                },
            ];

            if (integration.data?.testRunNameKeywords && integration.data?.testRunNameKeywords.length > 0) {
                fields.push({
                    label: 'Test run naming keywords',
                    displayString: integration.data?.testRunNameKeywords.join(', '),
                });
            }

            return fields;
        }
        case IntegrationType.BrowserStack: {
            const fields = [
                {
                    label: 'Project',
                    displayString:
                        integration.data?.selectedProject ?
                            `${integration.data.selectedProject.name} (${integration.data.selectedProject.identifier})`
                        :   null,
                },
            ];

            if (integration.data?.testRunNameKeywords && integration.data?.testRunNameKeywords.length > 0) {
                fields.push({
                    label: 'Test run naming keywords',
                    displayString: integration.data?.testRunNameKeywords.join(', '),
                });
            }

            return fields;
        }
        case IntegrationType.Qase: {
            const fields = [
                {
                    label: 'Project',
                    displayString:
                        integration.data?.selectedProject ?
                            `${integration.data.selectedProject.title} (${integration.data.selectedProject.code})`
                        :   null,
                },
            ];

            if (integration.data?.testRunNameKeywords && integration.data?.testRunNameKeywords.length > 0) {
                fields.push({
                    label: 'Test run naming keywords',
                    displayString: integration.data?.testRunNameKeywords.join(', '),
                });
            }

            return fields;
        }
        case IntegrationType.Jenkins: {
            const integrationData = integration.data as JenkinsIntegrationData;
            const fields = [
                {
                    label: `${rcBuildName} job/workflow`,
                    displayString: integrationData?.workflowName ?? '', // TODO: replace this or case once this is properly implemented
                },
            ];

            if (integrationData?.subJobName) {
                fields.push({ label: `${rcBuildName} subjob`, displayString: integrationData.subJobName });
            }

            if (!isWorkflowCIStepEnabled && integrationData?.devWorkflowName) {
                fields.push({
                    label: 'Development job/workflow',
                    displayString: integration.data?.devWorkflowName,
                });
            }

            if (!isWorkflowCIStepEnabled && integrationData?.devSubJobName) {
                fields.push({ label: 'Development subjob', displayString: integrationData.devSubJobName });
            }

            if (!isWorkflowCIStepEnabled && integrationData?.prodWorkflowName) {
                fields.push({ label: 'Release job/workflow', displayString: integrationData.prodWorkflowName });
            }

            if (!isWorkflowCIStepEnabled && integrationData?.prodSubJobName) {
                fields.push({ label: 'Release subjob', displayString: integrationData.prodSubJobName });
            }

            return fields;
        }
        case IntegrationType.Jira: {
            const provider = integrationProvider as IntegrationProviderJira;
            const integrationData = integration.data as JiraIntegrationData;

            const doneStatusesDisplayString =
                provider ? provider.data?.doneStatuses.concat(integrationData.additionalDoneStatuses).join(', ') : '';
            const projectsDisplayString =
                integrationData?.autoSelectAllProjects == true ?
                    '(Auto-select/sync all)'
                :   integrationData?.selectedProjects
                        .map((project) => project.name + ' (' + project.key + ')')
                        .join(', ');

            const fields = [
                {
                    label: 'Projects',
                    displayString: projectsDisplayString,
                },
                {
                    label: 'Done statuses',
                    displayString: doneStatusesDisplayString,
                },
            ];

            if (integrationData?.customFields && integrationData?.customFields.length > 0) {
                fields.push({ label: 'Additional fields', displayString: integrationData.customFields.join(', ') });
            }

            return fields;
        }
        case IntegrationType.Asana: {
            const fields = [
                {
                    label: 'Projects',
                    displayString: integration.data?.selectedProjects.map((project) => project.name).join(', '),
                },
            ];

            if (integration.data?.additionalDoneSections?.length > 0) {
                fields.push({
                    label: 'Done Sections',
                    displayString: integration.data?.additionalDoneSections?.join(', '),
                });
            }

            return fields;
        }
        case IntegrationType.Pivotal: {
            return [
                {
                    label: 'Projects',
                    displayString: integration.data?.selectedProjects.map((project) => project.name).join(', '),
                },
            ];
        }
        case IntegrationType.Shortcut: {
            const provider = integrationProvider as IntegrationProviderShortcut;

            const displayString =
                provider ?
                    provider.data?.doneStatuses
                        .concat(integration.data.additionalDoneStatuses)
                        .map((state) => state.name)
                        .join(', ')
                :   '';

            return [
                {
                    label: 'Teams',
                    displayString: integration.data?.selectedGroups.map((group) => group.name).join(', '),
                },
                {
                    label: 'Done statuses',
                    displayString: displayString,
                },
            ];
        }
        case IntegrationType.Slack: {
            const fields = [
                {
                    label: 'Primary channel(s)',
                    displayString: integration.data?.primaryChannels?.map((chan) => chan.name).join(', '),
                },
            ];

            return fields;
        }
        case IntegrationType.CircleCI: {
            const collabString =
                integration.data?.collaboration ?
                    integration.data?.collaboration?.orgName + '/' + integration.data?.collaboration?.projectType
                :   '';

            const fields = [
                {
                    label: 'Org name/Project type',
                    displayString: collabString,
                },
                {
                    label: 'Repo name/Project ID',
                    displayString: integration.data?.repoName,
                },
                {
                    label: rcWorkflowLabel(),
                    displayString: integration.data?.workflowName,
                },
                {
                    label: 'Pipeline definition ID',
                    displayString: integration.data?.definitionId,
                },
            ];

            if (!isWorkflowCIStepEnabled && integration.data?.devWorkflowName) {
                fields.push({
                    label: 'Development workflow',
                    displayString: integration.data?.devWorkflowName,
                });
            }

            if (!isWorkflowCIStepEnabled && integration.data?.prodWorkflowName) {
                fields.push({ label: prodWorkflowLabel(), displayString: integration.data.prodWorkflowName });
            }

            return fields;
        }
        case IntegrationType.Amazon: {
            return [
                {
                    label: 'App ID',
                    displayString: integration.data?.selectedAppId,
                },
            ];
        }
        case IntegrationType.Google: {
            const fields = [
                {
                    label: 'Package name',
                    displayString: integration.data?.packageName,
                },
                {
                    label: 'Developer account ID',
                    displayString: integration.data?.developerId,
                },
                {
                    label: 'Play Console email forwarding address',
                    displayString: (
                        <Space>
                            <Text>{storeEmailForwardingAddress}</Text>
                            <Button
                                icon={<LinkOutlined style={{ color: runwayColors.primary }} />}
                                type="link"
                                onClick={() => {
                                    navigator.clipboard.writeText(storeEmailForwardingAddress);
                                    globalMessage.success('Address copied to clipboard!');
                                    AnalyticEvents.track(
                                        AnalyticEvents.Event.ClickCopyStoreEmailForwardingAddressLink,
                                        trackingProperties,
                                    );
                                }}
                            ></Button>
                        </Space>
                    ),
                },
            ];

            if (integration.data?.lastReceivedForwardedEmailAt) {
                const lastReceivedAt = dayjs(integration.data.lastReceivedForwardedEmailAt);
                fields.push({
                    label: 'Play Console email forwarding',
                    displayString: (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            Active (Last received:{' '}
                            <TimeWithConditionalTz
                                triggeredAtLabel={formatDateWithTimezone(
                                    integration.data.lastReceivedForwardedEmailAt,
                                    'shortWithYearAndComma',
                                    effectiveTimezone,
                                    use24HourTimeFormat,
                                )}
                                triggeredByMoment={lastReceivedAt}
                                effectiveTimezone={effectiveTimezone}
                                use24HourTimeFormat={!!use24HourTimeFormat}
                            />
                            )
                        </div>
                    ),
                });
            }

            return fields;
        }
        case IntegrationType.HuaweiAppGallery:
            return [
                {
                    label: 'App ID',
                    displayString: integration.data?.appId,
                },
            ];
        case IntegrationType.GoogleBeta:
            return [
                {
                    label: 'Package name',
                    displayString: integration.data?.packageName,
                },
                {
                    label: 'Developer account ID',
                    displayString: integration.data?.developerId,
                },
                {
                    label: 'Beta testing track',
                    displayString: integration.data?.testingTrack,
                },
            ];
        case IntegrationType.Linear: {
            const provider = integrationProvider as IntegrationProviderLinear;

            const teamsDisplay = integration.data?.selectedTeams.map((team) => team.name).join(', ');
            const doneDisplay =
                provider ? provider.data?.doneStatuses.concat(integration.data.additionalDoneStatuses).join(', ') : '';

            return [
                {
                    label: 'Teams',
                    displayString: teamsDisplay,
                },
                {
                    label: 'Done statuses',
                    displayString: doneDisplay,
                },
            ];
        }
        case IntegrationType.TravisCI: {
            const fields = [
                {
                    label: 'Repo slug',
                    displayString: integration.data?.repoSlug ?? '',
                },
            ];

            return fields;
        }
        case IntegrationType.Firebase:
            return [
                {
                    label: 'Project ID',
                    displayString: integration.data?.projectId ?? '',
                },
                {
                    label: 'GA Property ID',
                    displayString: integration.data?.gaPropertyId ?? '',
                },
                {
                    label: 'Estimated BigQuery cost (on-demand compute)',
                    displayString:
                        integration.data?.estimatedMonthlyCost ?
                            '$' + integration.data.estimatedMonthlyCost.toFixed(2) + ' per month'
                        :   'Unavailable',
                },
            ];
        case IntegrationType.Xray: {
            const displayString =
                integration.data?.testRunNameKeywords && integration.data?.testRunNameKeywords.length > 0 ?
                    integration.data?.testRunNameKeywords.join(', ')
                :   'None';

            const fields = [
                {
                    label: 'Test execution naming keywords',
                    displayString: displayString,
                },
            ];

            return fields;
        }
        case IntegrationType.AppCenterCI: {
            const displayString =
                integration.data?.selectedApp ?
                    integration.data?.selectedApp?.appSlug + ' (' + integration.data?.selectedApp?.ownerName + ')'
                :   'None selected';
            const fields = [
                {
                    label: 'App Center App',
                    displayString: displayString,
                },
            ];

            return fields;
        }
        case IntegrationType.AppCenterBeta: {
            const displayString =
                integration.data?.selectedApp ?
                    integration.data?.selectedApp?.appSlug + ' (' + integration.data?.selectedApp?.ownerName + ')'
                :   'None selected';
            const fields = [
                {
                    label: 'App Center App',
                    displayString: displayString,
                },
            ];
            return fields;
        }
        case IntegrationType.Amplitude: {
            const displayString = listEventsAndParams(integration.data?.selectedEventsAndParams);
            const fields = [
                {
                    label: obaaLabelForIntegrationType(integration.id),
                    displayString: displayString,
                },
            ];

            return fields;
        }

        case IntegrationType.Optimizely: {
            const displayString =
                integration.data?.selectedProject ? integration.data?.selectedProject?.name : 'No project selected';

            const productionEnvironment =
                integration.data?.selectedEnvironment ?
                    integration.data?.selectedEnvironment?.name
                :   'No environment selected';

            const versionAttributeName =
                integration.data?.selectedVersionAttributeName ?? 'No version attribute selected';

            return [
                {
                    label: 'Selected project',
                    displayString: displayString,
                },
                {
                    label: 'Production environment',
                    displayString: productionEnvironment,
                },
                {
                    label: 'Release version attribute name',
                    displayString: versionAttributeName,
                },
            ];
        }

        case IntegrationType.LaunchDarkly: {
            const displayString =
                integration.data?.selectedProject ? integration.data?.selectedProject?.name : 'No project selected';

            const productionEnvironment =
                integration.data?.selectedEnvironment ?
                    integration.data?.selectedEnvironment?.name
                :   'No environment selected';

            const selectedTags =
                integration.data?.selectedTags?.length > 0 ? integration.data?.selectedTags?.join(', ') : 'All';

            const versionAttributeName =
                integration.data?.selectedVersionAttributeName ?? 'No version attribute selected';

            return [
                {
                    label: 'Selected project',
                    displayString: displayString,
                },
                {
                    label: 'Production environment',
                    displayString: productionEnvironment,
                },
                {
                    label: 'Tags',
                    displayString: selectedTags,
                },
                {
                    label: 'Release version attribute name',
                    displayString: versionAttributeName,
                },
            ];
        }

        case IntegrationType.Statsig: {
            const currentProjectName = integration?.data?.selectedProject?.name ?? 'unknown';
            const productionEnvironment =
                integration.data?.selectedEnvironment ?
                    integration.data?.selectedEnvironment?.name
                :   'No environment selected';
            const selectedTags =
                integration.data?.selectedTags?.length > 0 ? integration.data?.selectedTags?.join(', ') : 'All';
            const versionAttributeName =
                integration.data?.selectedVersionAttributeName ?? 'No version attribute selected';

            return [
                {
                    label: 'Project',
                    displayString: currentProjectName,
                },
                {
                    label: 'Production environment',
                    displayString: productionEnvironment,
                },
                {
                    label: 'Release version attribute name',
                    displayString: versionAttributeName,
                },
                {
                    label: 'Tags',
                    displayString: selectedTags,
                },
            ];
        }

        case IntegrationType.DatadogAnalytics: {
            const displayString = listEventsAndParams(integration.data?.selectedEventsAndParams);
            return [
                {
                    label: obaaLabelForIntegrationType(integration.id),
                    displayString: displayString,
                },
                {
                    label: 'Filter by Bundle ID',
                    displayString: integration.data?.shouldFilterByBundleId ? 'Yes' : 'No',
                },
            ];
        }
        case IntegrationType.GoogleAnalytics: {
            const displayString = listEventsAndParams(integration.data?.selectedEventsAndParams);
            return [
                {
                    label: obaaLabelForIntegrationType(integration.id),
                    displayString: displayString,
                },
                {
                    label: 'Data stream ID',
                    displayString: integration.data?.dataStreamId ?? 'No data stream ID set',
                },
            ];
        }
        case IntegrationType.AndroidVitals: {
            const displayString = listEventsAndParams(integration.data?.selectedEventsAndParams);
            return [
                {
                    label: obaaLabelForIntegrationType(integration.id),
                    displayString: displayString,
                },
            ];
        }
        case IntegrationType.ApplePowerPerformanceMetrics: {
            const displayString = listEventsAndParams(integration.data?.selectedEventsAndParams);
            return [
                {
                    label: obaaLabelForIntegrationType(integration.id),
                    displayString: displayString,
                },
            ];
        }
        case IntegrationType.GenericCI: {
            const fields = [
                {
                    label: rcWorkflowLabel(),
                    displayString: integration.data?.workflowName,
                },
            ];

            if (!isWorkflowCIStepEnabled && integration.data?.devWorkflowName) {
                fields.push({
                    label: 'Development workflow',
                    displayString: integration.data?.devWorkflowName,
                });
            }

            if (!isWorkflowCIStepEnabled && integration.data?.prodWorkflowName) {
                fields.push({ label: prodWorkflowLabel(), displayString: integration.data.prodWorkflowName });
            }

            return fields;
        }

        case IntegrationType.MixpanelAnalytics: {
            const displayString = listEventsAndParams(integration.data?.selectedEventsAndParams);
            const fields = [
                {
                    label: obaaLabelForIntegrationType(integration.id),
                    displayString: displayString,
                },
            ];

            return fields;
        }

        case IntegrationType.XcodeCloud: {
            const fields = [
                {
                    label: rcWorkflowLabel(),
                    displayString: integration.data?.productName?.concat('/', integration.data?.workflowName ?? ''),
                },
            ];

            if (!isWorkflowCIStepEnabled && integration.data?.devWorkflowName) {
                fields.push({
                    label: 'Development workflow',
                    displayString: integration.data?.devProductName?.concat('/', integration.data?.devWorkflowName),
                });
            }

            if (!isWorkflowCIStepEnabled && integration.data?.prodWorkflowName) {
                fields.push({
                    label: prodWorkflowLabel(),
                    displayString: integration.data?.prodProductName?.concat('/', integration.data?.prodWorkflowName),
                });
            }

            return fields;
        }

        case IntegrationType.Monday: {
            const provider = integrationProvider as IntegrationProviderMonday;
            const allBoards = provider.data.availableBoards;

            const displayStringForBoardColumnPair = (
                allBoards: MondayBoard[],
                boardColumnPairs: MondayBoardColumnPair[],
            ) => {
                const displayStrings = boardColumnPairs.map((boardColumnPair) => {
                    const board = allBoards.find((board) => boardColumnPair.boardId === board.id);
                    const column = board?.columns?.find((column) => column.id === boardColumnPair.columnId);

                    if (board && column) {
                        return `${column.name} (${board.name})`;
                    }

                    return 'Unknown';
                });

                return displayStrings;
            };

            const doneColumnsDisplayString =
                integration.data.doneColumns ?
                    displayStringForBoardColumnPair(allBoards, integration.data.doneColumns).join(', ')
                :   'No columns selected';
            const faColumnsDisplayString =
                integration.data.faColumns ?
                    displayStringForBoardColumnPair(allBoards, integration.data.faColumns).join(', ')
                :   'No columns selected';

            const fields = [
                {
                    label: 'Boards',
                    displayString: integration.data?.selectedBoards?.map((board) => board.name).join(', '),
                },
                {
                    label: 'Feature affiliation columns',
                    displayString: faColumnsDisplayString,
                },
                {
                    label: 'Done columns',
                    displayString: doneColumnsDisplayString,
                },
                {
                    label: 'Additional Done statuses',
                    displayString: integration.data.additionalDoneStatuses?.join(', '),
                },
            ];

            return fields;
        }

        case IntegrationType.FirebaseBeta: {
            const provider = integrationProvider as IntegrationProviderGoogle;
            const firebaseBeta = integration as IntegrationFirebaseBeta;

            if (!provider || !firebaseBeta) {
                return [];
            }

            const projectResources = provider.data?.projectsResources.find(
                (item) => item.project.projectId === firebaseBeta.data?.projectId,
            );

            if (!projectResources) {
                return [];
            }
            const fields = [
                {
                    label: 'Project ID',
                    displayString: firebaseBeta.data?.projectId ?? '',
                },
                {
                    label: 'App ID',
                    displayString: integration.data?.firebaseAppId ?? '',
                },
            ];

            return fields;
        }
        case IntegrationType.RunwayBuildDistroBeta: {
            const fields = [
                {
                    label: 'Selected Bucket',
                    displayString: integration.data?.selectedBucket?.name ?? '',
                },
            ];

            return fields;
        }

        case IntegrationType.AzureVCS: {
            const fields = [
                {
                    label: 'Project',
                    displayString:
                        integration.data?.selectedProject ?
                            integration.data?.selectedProject?.orgId.concat(
                                '/',
                                integration.data?.selectedProject?.name,
                            )
                        :   '',
                },
                {
                    label: `Repo`,
                    displayString: integration.data?.selectedRepo?.name ?? '',
                },
            ];

            return fields;
        }

        case IntegrationType.MicrosoftTeams: {
            const fields = [
                {
                    label: 'Primary channel(s)',
                    displayString: integration.data?.primaryChannels?.map((chan) => chan.displayName).join(', '),
                },
            ];

            return fields;
        }

        case IntegrationType.DatadogStabilityMonitoring: {
            const fields = [
                { label: 'Production environment', displayString: integration.data?.prodEnvironment ?? '' },
                {
                    label: 'Filter by Bundle ID',
                    displayString: integration.data?.shouldFilterByBundleId ? 'Yes' : 'No',
                },
            ];

            if (integration.data?.betaEnvironment) {
                fields.push({ label: 'Beta environment', displayString: integration.data.betaEnvironment });
            }

            return fields;
        }

        case IntegrationType.Crowdin: {
            return [
                { label: 'Project', displayString: integration.data?.selectedProject?.name ?? '' },
                {
                    label: 'Metadata project',
                    displayString: integration.data.selectedMetadataProject?.name ?? 'Not set',
                },
            ];
        }

        case IntegrationType.Lokalise: {
            return [
                { label: 'Project', displayString: integration.data?.selectedProject?.name ?? '' },
                {
                    label: 'Metadata project',
                    displayString: integration.data.selectedMetadataProject?.name ?? 'Not set',
                },
            ];
        }

        case IntegrationType.PagerDutyScheduling: {
            return [
                {
                    label: 'Schedule',
                    labelExtra: schedulingIntegrationLabelExtra(),
                    displayString: integration.data?.selectedSchedule?.name ?? '',
                },
            ];
        }

        case IntegrationType.OpsgenieScheduling: {
            return [
                {
                    label: 'Schedule',
                    labelExtra: schedulingIntegrationLabelExtra(),
                    displayString: integration.data?.selectedSchedule?.name ?? '',
                },
                { label: 'Rotation', displayString: integration.data?.selectedRotation?.name ?? '' },
            ];
        }

        case IntegrationType.JiraServiceManagementScheduling: {
            return [
                {
                    label: 'Schedule',
                    labelExtra: schedulingIntegrationLabelExtra(),
                    displayString: integration.data?.selectedSchedule?.name ?? '',
                },
                { label: 'Rotation', displayString: integration.data?.selectedRotation?.name ?? '' },
            ];
        }

        case IntegrationType.NewRelicStabilityMonitoring: {
            const fields = [
                { label: 'App Name', displayString: integration.data.prodMobileApplication.name ?? '' },
                { label: 'App ID', displayString: integration.data.prodMobileApplication.appId.toString() ?? '' },
            ];

            if (integration.data.betaMobileApplication.name !== '') {
                fields.push({
                    label: 'Beta App Name',
                    displayString: integration.data.betaMobileApplication.name,
                });
                fields.push({
                    label: 'Beta App ID',
                    displayString: integration.data.betaMobileApplication.appId.toString(),
                });
            }

            return fields;
        }

        case IntegrationType.NewRelicAnalytics: {
            const displayString = listEventsAndParams(integration.data?.selectedEventsAndParams);
            return [
                {
                    label: obaaLabelForIntegrationType(integration.id),
                    displayString: displayString,
                },
                { label: 'App Name', displayString: integration.data.application.name ?? '' },
                { label: 'App ID', displayString: integration.data.application.appId.toString() ?? '' },
            ];
        }

        case IntegrationType.Samsung: {
            return [
                {
                    label: 'Content ID',
                    displayString: integration.data?.appId,
                },
            ];
        }

        case IntegrationType.DynatraceStabilityMonitoring: {
            const dynatraceIntegrationProvider = integrationProvider as IntegrationProviderDynatrace;
            const fields = [
                { label: 'Environment ID', displayString: dynatraceIntegrationProvider.data?.environmentId ?? '' },
                { label: 'Production RUM application', displayString: integration.data?.prodRumApp?.name ?? '' },
            ];

            if (integration.data?.betaRumApp) {
                fields.push({
                    label: 'Beta RUM application',
                    displayString: integration.data?.betaRumApp?.name ?? '',
                });
            }

            return fields;
        }

        case IntegrationType.CustomAnalytics: {
            const detectedEvents = integration.data?.detectedEvents ?? [];
            const integrationId = integration.data?.integrationId ?? '';
            return [
                {
                    label: 'Integration ID',
                    displayString: (
                        <div style={{ position: 'relative', left: -10, top: 5 }}>
                            <Text code style={codeStyle('15px')}>
                                {integrationId}
                            </Text>
                            <Button
                                onClick={() => copyToClipboard(integrationId)}
                                icon={<CopyOutlined style={{ color: antGrey.grey6 }} />}
                                type="link"
                                style={{ width: 28, position: 'relative', top: -2, zIndex: 0 }}
                            />
                        </div>
                    ),
                },
                {
                    label: 'Events detected',
                    displayString: detectedEvents.length > 0 ? detectedEvents.join(', ') : 'No events detected',
                },
                {
                    label: 'Data last received at',
                    displayString:
                        integration.data?.dataLastReceivedAt ?
                            dayjs(integration.data?.dataLastReceivedAt)
                                .tz(effectiveTimezone)
                                .format(DatetimeFormat.DayAbbreviatedMonthDateYearTimeAbbreviated)
                        :   'No data yet',
                },
            ];
        }

        case IntegrationType.CustomCI: {
            const integrationId = integration.data?.integrationId ?? '';
            return [
                {
                    label: 'Integration ID',
                    displayString: (
                        <div style={{ position: 'relative', left: -10, top: 5 }}>
                            <Text code style={codeStyle('15px')}>
                                {integrationId}
                            </Text>
                            <Button
                                onClick={() => copyToClipboard(integrationId)}
                                icon={<CopyOutlined style={{ color: antGrey.grey6 }} />}
                                type="link"
                                style={{ width: 28, position: 'relative', top: -2, zIndex: 0 }}
                            />
                        </div>
                    ),
                },
                {
                    label: 'Data last received at',
                    displayString:
                        integration.data?.dataLastReceivedAt ?
                            dayjs(integration.data?.dataLastReceivedAt)
                                .tz(effectiveTimezone)
                                .format(DatetimeFormat.DayAbbreviatedMonthDateYearTimeAbbreviated)
                        :   'No data yet',
                },
            ];
        }

        case IntegrationType.GoogleCalendar:
        case IntegrationType.GooglePersonalCalendar: {
            const calendarData = integration.data as GoogleCalendarIntegrationData;

            if (!calendarData) {
                return [];
            }

            return [
                {
                    label: 'Calendar name',
                    displayString: calendarData?.calendarName ?? '',
                },
                {
                    label: 'Calendar ID',
                    displayString: (
                        <Text ellipsis={true} style={{ width: 250 }}>
                            {calendarData.calendarId}
                        </Text>
                    ),
                },
                {
                    label: 'App name on calendar events',
                    displayString: calendarData?.appDisplayName ?? '',
                },
            ];
        }

        default:
            return [];
    }
};

const obaaLabelForIntegrationType = (integrationId: IntegrationType) => {
    const [, propertyPluralName] = eventPropertyTerminology(integrationId);
    return `Events and ${propertyPluralName} selected for monitoring`;
};
